/**
 * Поиск полей карточки, в которые приложение пишет профиль дела.
 *
 * Дела ведутся в сделках, а пользовательским полям сделки Битрикс24 присваивает
 * коды сам: `UF_CRM_1758312045`. Осмысленного кода у них нет вовсе — задать его
 * при создании поля нельзя, — и угадать имя невозможно.
 *
 * Поэтому поля ищутся **по названию**: название задаёт администратор, оно же
 * видно на экране. Для смарт-процессов, где код задать можно, остаётся запасной
 * способ — сравнение по окончанию кода.
 *
 * Зачем всё это. Запись в несуществующее поле портал принимает МОЛЧА:
 * приложение отчиталось бы «Сохранено», а дело ушло бы в пустоту. Такую
 * ошибку нельзя обнаруживать на потерянной работе юриста.
 */

import { call } from './rest.js';
import { CASE_FIELDS, DEFAULT_CASE_FIELDS, applyCaseFieldNames, type CaseFieldKey } from './cases.js';

export interface FieldReport {
  key: CaseFieldKey;
  title: string;
  /** Имя, которым приложение пользуется сейчас */
  expected: string;
  ok: boolean;
  /** Найденное в портале поле */
  candidate: string | null;
  type: string | null;
  /** Многострочное ли поле — критично для профиля */
  multiline: boolean | null;
}

export interface FieldsCheck {
  entityTypeId: number;
  reachable: boolean;
  error: string | null;
  fields: FieldReport[];
  suggestion: Partial<Record<CaseFieldKey, string>> | null;
  /** Поля, которые обрежут содержимое */
  truncationRisks: string[];
  /** Чего в карточке нет вовсе — их должен создать администратор */
  missing: string[];
}

/**
 * Названия полей, которые администратор создаёт в карточке сделки.
 * Они же перечислены в docs/bitrix-setup.md — списки должны совпадать.
 */
const TITLES: Record<CaseFieldKey, string> = {
  profile: 'Профиль дела',
  version: 'Редакция профиля',
  courtName: 'Суд',
  debtTotal: 'Сумма задолженности',
  creditorsCount: 'Кредиторов',
  hasProperty: 'Есть имущество',
  packageStatus: 'Статус пакета',
  lastGeneratedAt: 'Последняя генерация',
  folderId: 'Папка дела',
};

/** Профиль дела занимает десятки килобайт: однострочное поле его обрежет. */
const MUST_BE_MULTILINE: CaseFieldKey[] = ['profile'];

interface PortalField {
  title?: string;
  formLabel?: string;
  listLabel?: string;
  type?: string;
  settings?: { rows?: number } & Record<string, unknown>;
}

const labelOf = (field: PortalField): string => field.title ?? field.formLabel ?? field.listLabel ?? '';

/** Регистр, пробелы и знаки препинания различий не образуют. */
export function normalizeTitle(title: string): string {
  return title
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[^\p{L}\p{N}]/gu, '');
}

/**
 * Поиск поля по названию. Единственное совпадение — оно и есть; несколько
 * одинаковых названий означают, что выбирать наугад нельзя.
 */
export function findByTitle(title: string, fields: Record<string, PortalField>): string | null {
  const wanted = normalizeTitle(title);
  const matches = Object.entries(fields).filter(([, field]) => normalizeTitle(labelOf(field)) === wanted);
  return matches.length === 1 ? matches[0]![0] : null;
}

/**
 * Запасной способ для смарт-процессов: `ufCrmProfileJson` и
 * `ufCrm1032ProfileJson` различаются вставленным номером.
 */
export function findByCode(expected: string, names: string[]): string | null {
  const tail = expected.replace(/^ufCrm/i, '').toLowerCase();
  if (!tail) return null;
  const matches = names.filter((name) => name.toLowerCase().endsWith(tail));
  return matches.length === 1 ? matches[0]! : null;
}

export async function checkFields(entityTypeId: number): Promise<FieldsCheck> {
  const empty: FieldsCheck = {
    entityTypeId,
    reachable: false,
    error: null,
    fields: [],
    suggestion: null,
    truncationRisks: [],
    missing: [],
  };

  if (!entityTypeId) {
    return { ...empty, error: 'Приложение открыто вне карточки дела: карточка неизвестна' };
  }

  let portal: Record<string, PortalField>;
  try {
    const response = await call<{ fields: Record<string, PortalField> }>('crm.item.fields', { entityTypeId });
    portal = response?.fields ?? {};
  } catch (error) {
    return { ...empty, error: `Карточка недоступна: ${(error as Error).message}` };
  }

  const names = Object.keys(portal);
  const suggestion: Partial<Record<CaseFieldKey, string>> = {};
  const truncationRisks: string[] = [];
  const missing: string[] = [];

  const fields = (Object.keys(DEFAULT_CASE_FIELDS) as CaseFieldKey[]).map((key): FieldReport => {
    const expected = CASE_FIELDS[key];
    const exact = portal[expected];

    // Сначала по названию: у полей сделки осмысленного кода нет
    const candidate = exact ? null : findByTitle(TITLES[key], portal) ?? findByCode(DEFAULT_CASE_FIELDS[key], names);
    if (candidate) suggestion[key] = candidate;

    const description = exact ?? (candidate ? portal[candidate] : undefined);
    if (!description) missing.push(TITLES[key]);

    const rows = Number(description?.settings?.rows ?? 0);
    const multiline = description ? rows > 1 || description.type === 'text' : null;

    if (MUST_BE_MULTILINE.includes(key) && multiline === false) {
      truncationRisks.push(`«${TITLES[key]}» — поле однострочное. Содержимое обрежется, дело будет потеряно.`);
    }

    return {
      key,
      title: TITLES[key],
      expected,
      ok: Boolean(exact),
      candidate,
      type: description?.type ?? null,
      multiline,
    };
  });

  return {
    entityTypeId,
    reachable: true,
    error: null,
    fields,
    suggestion: Object.keys(suggestion).length > 0 ? suggestion : null,
    truncationRisks,
    missing,
  };
}

/**
 * Уточнение имён при открытии карточки: одно обращение к порталу,
 * снимающее целый класс молчаливых потерь.
 */
export async function resolveFieldNames(entityTypeId: number): Promise<FieldsCheck | null> {
  const check = await checkFields(entityTypeId).catch(() => null);
  if (check?.suggestion) applyCaseFieldNames(check.suggestion);
  return check;
}
